import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './App.css';
import logo from './assets/logo.webp';

// Service images
import webDevImg from './assets/images/services/web-dev.webp';
import mobileDevImg from './assets/images/services/mobile-dev.webp';
import aiSolutionsImg from './assets/images/services/ai-solutions.webp';
import digitalMarketingImg from './assets/images/services/digital-marketing.webp';

// Hero background
import heroBg from './assets/images/hero/hero-bg.webp';

/* // About section images
import teamImg from './assets/images/about/team.webp';
import officeImg from './assets/images/about/office.webp'; */

// About icons
import focusedExpertiseIcon from './assets/icons/about/focused-expertise.webp';
import speedEfficiencyIcon from './assets/icons/about/speed-efficiency.webp';
import partnershipIcon from './assets/icons/about/partnership.webp';

// Philosophy icons
import timeIcon from './assets/icons/philosophy/time.webp';
import effortIcon from './assets/icons/philosophy/effort.webp';
import technologyIcon from './assets/icons/philosophy/technology.webp';

import visionIcon from './assets/icons/vision/vision.webp';
import missionIcon from './assets/icons/vision/mission.webp';

// Strengths icons
import deepExpertiseIcon from './assets/icons/strengths/deep-expertise.webp';
import automationIcon from './assets/icons/strengths/automation.webp';
import aiIntegrationIcon from './assets/icons/strengths/ai-integration.webp';
import scalableIcon from './assets/icons/strengths/scalable.webp';
import communicationIcon from './assets/icons/strengths/communication.webp';
import domainAIIcon from './assets/icons/strengths/domain-ai.webp';

// Contact icons
import emailIcon from './assets/icons/contact/email.webp';
import callIcon from './assets/icons/contact/call.webp';
import visitIcon from './assets/icons/contact/visit.webp';
import hoursIcon from './assets/icons/contact/hours.webp';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className = '' }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [workHours, setWorkHours] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Calculate work hours since 2024
  useEffect(() => {
    const startDate = new Date('2024-01-01');
    const currentDate = new Date();
    const diffTime = Math.abs(currentDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    // Assuming 10 working hours per day, 6 days a week
    const workingDays = Math.floor(diffDays * (6 / 7));
    const totalHours = workingDays * 10;
    setWorkHours(totalHours);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="app">
      {/* Header */}
      <header className={`header ${scrolled ? 'scrolled' : ''}`}>
        <nav className="nav-container">
          <div className="logo-container">
            <img
              src={logo}
              alt="Spyyda Tech Solutions"
              className="logo"
              onClick={() => scrollToSection('home')}
              style={{ cursor: 'pointer' }}
            />
          </div>

          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            ☰
          </button>

          <ul className={`nav-menu ${mobileMenuOpen ? 'active' : ''}`}>
            <li><a href="#home" className="nav-link" onClick={() => scrollToSection('home')}>Home</a></li>
            <li><a href="#about" className="nav-link" onClick={() => scrollToSection('about')}>About</a></li>
            <li><a href="#services" className="nav-link" onClick={() => scrollToSection('services')}>Services</a></li>
            <li><a href="#why-us" className="nav-link" onClick={() => scrollToSection('why-us')}>Why Us</a></li>
            <li><a href="#testimonials" className="nav-link" onClick={() => scrollToSection('testimonials')}>Testimonials</a></li>
            <li><a href="#contact" className="nav-link" onClick={() => scrollToSection('contact')}>Contact</a></li>
          </ul>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div
          className="hero-background"
          style={{
            backgroundImage: `url(${heroBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="hero-grid"></div>
          <div className="hero-glow"></div>
        </div>

        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <motion.p
            className="hero-subtitle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            SPYYDA TECH SOLUTIONS &middot; COIMBATORE
          </motion.p>

          <motion.h1
            className="hero-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Making Human Time<br />Truly Valuable
          </motion.h1>

          <motion.p
            className="hero-description"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            Spyyda Tech Solutions is a digital marketing agency, software development company and AI products startup in Coimbatore.
            We build websites, mobile apps, automation and AI-powered solutions that reduce effort and deliver measurable time savings.
          </motion.p>

          <motion.div
            className="hero-cta"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
          >
            <a href="#contact" className="btn btn-primary" onClick={() => scrollToSection('contact')}>
              Start Your Project
            </a>
            <a href="#about" className="btn btn-secondary" onClick={() => scrollToSection('about')}>
              Learn More
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* About Section */}
      <section id="about" className="section">
        <AnimatedSection>
          <div className="section-header">
            <p className="section-label">WHO WE ARE</p>
            <h2 className="section-title">Engineering Excellence, Amplifying Value</h2>
            <p className="section-description">
              Spyyda Tech Solutions is a forward-thinking technology company specializing in custom software development,
              AI integration, and digital transformation. Founded in 2024, we've rapidly established ourselves as a trusted
              partner for businesses seeking to leverage technology for competitive advantage.
            </p>
          </div>
        </AnimatedSection>

        <AnimatedSection>
          <div style={{
            background: 'var(--secondary-black)',
            padding: '3rem',
            borderRadius: '20px',
            marginTop: '3rem',
            border: '1px solid rgba(255, 215, 0, 0.1)'
          }}>
            <h3 style={{
              fontSize: '2rem',
              marginBottom: '2rem',
              color: 'var(--accent-yellow)'
            }}>
              What Sets Us Apart
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '2rem'
            }}>
              <div>
                <img src={focusedExpertiseIcon} alt="Focused Expertise" className="about-icon" />
                <h4 style={{ color: 'var(--white)', fontSize: '1.3rem', marginBottom: '1rem' }}>
                  Focused Expertise
                </h4>
                <p style={{ color: 'var(--light-gray)', lineHeight: '1.8' }}>
                  We don't just build software; we architect solutions that solve real business problems.
                  Our team combines deep technical knowledge with business acumen to deliver results that matter.
                </p>
              </div>
              <div>
                <img src={speedEfficiencyIcon} alt="Speed & Efficiency" className="about-icon" />
                <h4 style={{ color: 'var(--white)', fontSize: '1.3rem', marginBottom: '1rem' }}>
                  Speed & Efficiency
                </h4>
                <p style={{ color: 'var(--light-gray)', lineHeight: '1.8' }}>
                  Time is our most valuable resource. We've streamlined our development process to deliver
                  high-quality solutions faster, without compromising on quality or functionality.
                </p>
              </div>
              <div>
                <img src={partnershipIcon} alt="Partnership Approach" className="about-icon" />
                <h4 style={{ color: 'var(--white)', fontSize: '1.3rem', marginBottom: '1rem' }}>
                  Partnership Approach
                </h4>
                <p style={{ color: 'var(--light-gray)', lineHeight: '1.8' }}>
                  We view every client relationship as a partnership. Your success is our success, and we're
                  committed to being a long-term technology partner, not just a vendor.
                </p>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* <AnimatedSection>
          <div className="about-images">
            <div className="about-image-card">
              <img src={teamImg} alt="Spyyda Tech Solutions team in Coimbatore" />
              <div className="about-image-caption">Our Expert Team</div>
            </div>
            <div className="about-image-card">
              <img src={officeImg} alt="Spyyda Tech Solutions office, Ramanathapuram, Coimbatore" />
              <div className="about-image-caption">Our Workspace</div>
            </div>
          </div>
        </AnimatedSection> */}

        <motion.div
          className="stats-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.div className="stat-card" variants={fadeInUp}>
            <div className="stat-number">{workHours.toLocaleString()}+</div>
            <div className="stat-label">Work Hours Invested</div>
          </motion.div>

          <motion.div className="stat-card" variants={fadeInUp}>
            <div className="stat-number">50+</div>
            <div className="stat-label">Projects Delivered</div>
          </motion.div>

          <motion.div className="stat-card" variants={fadeInUp}>
            <div className="stat-number">100%</div>
            <div className="stat-label">Client Satisfaction</div>
          </motion.div>

          <motion.div className="stat-card" variants={fadeInUp}>
            <div className="stat-number">24/7</div>
            <div className="stat-label">Support Available</div>
          </motion.div>
        </motion.div>
      </section>

      {/* Services Section */}
      <section id="services" className="section">
        <AnimatedSection>
          <div className="section-header">
            <p className="section-label">WHAT WE DO</p>
            <h2 className="section-title">Comprehensive Technology Solutions</h2>
            <p className="section-description">
              From web and mobile development to AI integration and digital marketing,
              we deliver end-to-end solutions that drive measurable results.
            </p>
          </div>
        </AnimatedSection>

        <motion.div
          className="services-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.div className="service-card" variants={fadeInUp}>
            <div className="service-icon">
              <img src={webDevImg} alt="Website development company in Coimbatore" />
            </div>
            <h3 className="service-title">Website Development</h3>
            <p className="service-description">
              Custom-built, responsive websites that combine stunning design with powerful functionality.
              From corporate sites to complex web applications, we create digital experiences that convert.
            </p>
          </motion.div>

          <motion.div className="service-card" variants={fadeInUp}>
            <div className="service-icon">
              <img src={mobileDevImg} alt="Mobile app development services" />
            </div>
            <h3 className="service-title">Mobile App Development</h3>
            <p className="service-description">
              Native and cross-platform mobile applications that deliver seamless user experiences.
              We build apps that users love, optimized for performance and engagement.
            </p>
          </motion.div>

          <motion.div className="service-card" variants={fadeInUp}>
            <div className="service-icon">
              <img src={aiSolutionsImg} alt="AI solutions and AI products for businesses" />
            </div>
            <h3 className="service-title">AI-Powered Solutions</h3>
            <p className="service-description">
              Intelligent automation, decision-support systems, and AI copilots that reduce manual effort
              and accelerate workflows. AI is our core capability, not an add-on.
            </p>
          </motion.div>

          <motion.div className="service-card" variants={fadeInUp}>
            <div className="service-icon">
              <img src={digitalMarketingImg} alt="Digital marketing agency in Coimbatore" />
            </div>
            <h3 className="service-title">Digital Marketing</h3>
            <p className="service-description">
              Strategic digital marketing campaigns that drive growth. SEO, content strategy,
              and analytics-driven optimization to maximize your online presence.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Vision & Mission Section */}
      <section className="section" style={{ background: 'var(--secondary-black)' }}>
        <AnimatedSection>
          <div className="section-header">
            <p className="section-label">OUR DIRECTION</p>
            <h2 className="section-title">Vision & Mission</h2>
          </div>
        </AnimatedSection>

        <motion.div
          className="vision-mission-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.div className="vm-card" variants={fadeInUp}>
            <h3 className="vm-title">
              <img src={visionIcon} alt="Vision" style={{ width: '50px', height: '50px', marginRight: '1rem' }} /> Vision
            </h3>
            <p className="vm-content">
              To make human time truly valuable. We envision a world where technology quietly removes
              friction from everyday work, freeing people and organizations to focus on ideas, decisions,
              and outcomes that matter most. By intelligently quantifying and optimizing time, we aim to
              amplify productivity, creativity, and meaningful impact.
            </p>
          </motion.div>

          <motion.div className="vm-card" variants={fadeInUp}>
            <h3 className="vm-title">
              <img src={missionIcon} alt="Mission" style={{ width: '50px', height: '50px', marginRight: '1rem' }} /> Mission
            </h3>
            <p className="vm-content">
              Our mission is to build technology that reduces effort, streamlines workflows, and delivers
              measurable time savings. Through thoughtful software engineering, automation, and AI-powered
              solutions, we help businesses move faster with confidence—transforming saved time into tangible
              value and growth.
            </p>
          </motion.div>
        </motion.div>

        <AnimatedSection>
          <div className="section-header" style={{ marginTop: '5rem' }}>
            <h3 className="section-title" style={{ fontSize: '2rem' }}>Our Philosophy</h3>
          </div>
        </AnimatedSection>

        <motion.div
          className="philosophy-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.div className="philosophy-card" variants={fadeInUp}>
            <div className="philosophy-icon">
              <img src={timeIcon} alt="Time is measurable" />
            </div>
            <p className="philosophy-text">Time is measurable</p>
          </motion.div>

          <motion.div className="philosophy-card" variants={fadeInUp}>
            <div className="philosophy-icon">
              <img src={effortIcon} alt="Effort is reducible" />
            </div>
            <p className="philosophy-text">Effort is reducible</p>
          </motion.div>

          <motion.div className="philosophy-card" variants={fadeInUp}>
            <div className="philosophy-icon">
              <img src={technologyIcon} alt="Technology amplifies value" />
            </div>
            <p className="philosophy-text">Technology should amplify human value</p>
          </motion.div>
        </motion.div>

        <AnimatedSection>
          <div style={{ textAlign: 'center', marginTop: '4rem' }}>
            <p style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--accent-yellow)' }}>
              Everything we build aligns with one belief:<br />
              <span style={{ color: 'var(--white)', fontSize: '1.8rem' }}>
                When effort decreases, value increases.
              </span>
            </p>
          </div>
        </AnimatedSection>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="section">
        <AnimatedSection>
          <div className="section-header">
            <p className="section-label">WHY CHOOSE US</p>
            <h2 className="section-title">Our Strengths</h2>
            <p className="section-description">
              We are good at what we do because we design solutions with real-world usage,
              performance, and long-term value in mind.
            </p>
          </div>
        </AnimatedSection>

        <motion.div
          className="strengths-list"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.div className="strength-item" variants={fadeInUp}>
            <div className="strength-icon">
              <img src={deepExpertiseIcon} alt="Deep Expertise" />
            </div>
            <p className="strength-text">
              Deep expertise in custom software development across web, mobile, and desktop platforms
            </p>
          </motion.div>

          <motion.div className="strength-item" variants={fadeInUp}>
            <div className="strength-icon">
              <img src={automationIcon} alt="Automation" />
            </div>
            <p className="strength-text">
              Strong focus on automation and workflow optimization that delivers measurable time savings
            </p>
          </motion.div>

          <motion.div className="strength-item" variants={fadeInUp}>
            <div className="strength-icon">
              <img src={aiIntegrationIcon} alt="AI Integration" />
            </div>
            <p className="strength-text">
              Practical, business-focused AI integration that reduces manual effort and improves accuracy
            </p>
          </motion.div>

          <motion.div className="strength-item" variants={fadeInUp}>
            <div className="strength-icon">
              <img src={scalableIcon} alt="Scalable Design" />
            </div>
            <p className="strength-text">
              Scalable and maintainable system design built for long-term growth and performance
            </p>
          </motion.div>

          <motion.div className="strength-item" variants={fadeInUp}>
            <div className="strength-icon">
              <img src={communicationIcon} alt="Clear Communication" />
            </div>
            <p className="strength-text">
              Clear communication and engineering-first mindset focused on delivering real value
            </p>
          </motion.div>

          <motion.div className="strength-item" variants={fadeInUp}>
            <div className="strength-icon">
              <img src={domainAIIcon} alt="Domain-specific AI" />
            </div>
            <p className="strength-text">
              Domain-specific AI copilots and decision-support systems designed for your unique needs
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="section" style={{ background: 'var(--secondary-black)' }}>
        <AnimatedSection>
          <div className="section-header">
            <p className="section-label">TESTIMONIALS</p>
            <h2 className="section-title">What Our Clients Say</h2>
            <p className="section-description">
              Don't just take our word for it. Here's what our clients have to say about working with us.
            </p>
          </div>
        </AnimatedSection>

        <motion.div
          className="testimonials-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.div className="testimonial-card" variants={fadeInUp}>
            <div className="quote-icon">"</div>
            <p className="testimonial-text">
              Spyyda Tech Solutions transformed our educational management system. The EduTrack Pro
              application they built has streamlined our operations across all five branches, saving
              us countless hours every week. Their attention to detail and understanding of our needs
              was exceptional.
            </p>
            <div className="testimonial-author">
              <div className="author-avatar">T</div>
              <div className="author-info">
                <div className="author-name">Tesla's Academy</div>
                <div className="author-title">Educational Institution</div>
              </div>
            </div>
          </motion.div>

          <motion.div className="testimonial-card" variants={fadeInUp}>
            <div className="quote-icon">"</div>
            <p className="testimonial-text">
              The website and booking system they developed for our travel agency exceeded our expectations.
              Not only does it look stunning, but the SEO optimization has significantly increased our
              online visibility. We've seen a 40% increase in inquiries since launch.
            </p>
            <div className="testimonial-author">
              <div className="author-avatar">P</div>
              <div className="author-info">
                <div className="author-name">Priya Travels</div>
                <div className="author-title">Travel Agency, Coimbatore</div>
              </div>
            </div>
          </motion.div>

          <motion.div className="testimonial-card" variants={fadeInUp}>
            <div className="quote-icon">"</div>
            <p className="testimonial-text">
              Working with Spyyda was a game-changer for our business. They delivered a comprehensive
              GST billing and inventory management system that handles all Indian tax compliance seamlessly.
              The desktop application is fast, reliable, and exactly what we needed.
            </p>
            <div className="testimonial-author">
              <div className="author-avatar">M</div>
              <div className="author-info">
                <div className="author-name">Metrox Power Industries</div>
                <div className="author-title">Manufacturing Company</div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section">
        <AnimatedSection>
          <div className="section-header">
            <p className="section-label">GET IN TOUCH</p>
            <h2 className="section-title">Let's Build Something Amazing</h2>
            <p className="section-description">
              Ready to transform your business with technology that amplifies value?
              Reach out to us and let's discuss your project.
            </p>
          </div>
        </AnimatedSection>

        <motion.div
          className="contact-container"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.div className="contact-info" variants={fadeInUp}>
            <div className="contact-item">
              <div className="contact-icon">
                <img src={emailIcon} alt="Email" />
              </div>
              <div className="contact-details">
                <h3>Email Us</h3>
                <p>
                  <a href="mailto:spyydadesk@spyydatech.com">spyydadesk@spyydatech.com</a>
                </p>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon">
                <img src={callIcon} alt="Call" />
              </div>
              <div className="contact-details">
                <h3>Call Us</h3>
                <p>
                  <a href="tel:+919514146499">+91 95 14 14 64 99</a>
                </p>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon">
                <img src={visitIcon} alt="Visit" />
              </div>
              <div className="contact-details">
                <h3>Visit Us</h3>
                <address style={{ fontStyle: 'normal' }}>Spyyda Tech Solutions LLP<br />2nd Floor, 78, Kongu Nagar,<br />Ramanathapuram, Coimbatore,<br />Tamil Nadu 641045</address>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon">
                <img src={hoursIcon} alt="Business Hours" />
              </div>
              <div className="contact-details">
                <h3>Business Hours</h3>
                <p>Monday - Saturday: 9:00 AM - 7:00 PM</p>
              </div>
            </div>
          </motion.div>

          <motion.div className="contact-form" variants={fadeInUp}>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const name = formData.get('name');
              const email = formData.get('email');
              const phone = formData.get('phone');
              const message = formData.get('message');

              const whatsappMessage = `*New Contact Form Submission*%0A%0A*Name:* ${encodeURIComponent(name)}%0A*Email:* ${encodeURIComponent(email)}%0A*Phone:* ${encodeURIComponent(phone || 'Not provided')}%0A*Message:* ${encodeURIComponent(message)}`;

              const whatsappNumber = '919514146499'; // Replace with your WhatsApp number
              const whatsappURL = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

              window.open(whatsappURL, '_blank');
              e.target.reset();
            }}>
              <div className="form-group">
                <label className="form-label">Your Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="John Doe"
                  name="name"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="john@example.com"
                  name="email"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input
                  type="tel"
                  className="form-input"
                  placeholder="+91 98765 43210"
                  name="phone"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Your Message</label>
                <textarea
                  className="form-textarea"
                  placeholder="Tell us about your project..."
                  name="message"
                  required
                ></textarea>
              </div>

              <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
                Send Message
              </button>
            </form>
          </motion.div>
        </motion.div>
      </section>

      {/* Footer */}
      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <img src={logo} alt="Spyyda Tech Solutions" className="footer-logo" />
            <p className="footer-tagline">
              Making human time truly valuable through intelligent technology solutions
              that amplify productivity and deliver measurable results.
            </p>

            {/* Social Media Links */}
            <div className="social-links">
              <a
                href="https://www.linkedin.com/company/spyyda-tech-solutions"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label="LinkedIn"
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </a>

              {/* <a
                href="https://twitter.com/spyydatech"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label="Twitter"
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>

              <a
                href="https://www.facebook.com/spyydatech"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label="Facebook"
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
                </svg>
              </a> */}

              <a
                href="https://www.instagram.com/spyydatech"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label="Instagram"
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>

              <a
                href="https://github.com/spyydatech"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label="GitHub"
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
              </a>

              <a
                href="https://wa.me/919514146499"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label="WhatsApp"
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                </svg>
              </a>
            </div>
          </div>

          <div className="footer-links">
            <h4>Quick Links</h4>
            <ul>
              <li><a href="#about" onClick={() => scrollToSection('about')}>About Us</a></li>
              <li><a href="#services" onClick={() => scrollToSection('services')}>Services</a></li>
              <li><a href="#why-us" onClick={() => scrollToSection('why-us')}>Why Choose Us</a></li>
              <li><a href="#contact" onClick={() => scrollToSection('contact')}>Contact</a></li>
            </ul>
          </div>

          <div className="footer-links">
            <h4>Services</h4>
            <ul>
              <li><a href="#services">Website Development</a></li>
              <li><a href="#services">Mobile Apps</a></li>
              <li><a href="#services">AI Solutions</a></li>
              <li><a href="#services">Digital Marketing</a></li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Spyyda Tech Solutions. All rights reserved. | Crafted with precision and passion.</p>
          <p style={{ marginTop: '0.5rem' }}>
            <a href="/privacy-policy" style={{ color: 'var(--accent-yellow)' }}>Privacy Policy</a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
